import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryClient } from './query-client';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { dedupeMeetingsByEvent } from './meeting-row-state';
import { foldMeetingPages } from './meeting-history-pages';
import type { UpcomingMeetingState } from './meeting-row-state';

export { dedupeMeetingsByEvent, meetingRowAction, meetingRowDecision, meetingRowKey, MEETING_STATE_BADGE } from './meeting-row-state';
export type { MeetingRowAction, UpcomingMeetingState } from './meeting-row-state';

/**
 * The Meetings channel's client contract — the shapes the three `/api/meetings/*` proxies answer with,
 * and the reads and writes the card is built on.
 *
 * The wire strings are spelled out here rather than imported from the common-lib enum on purpose: the
 * enum is a VALUE from that barrel, and in a worktree the barrel resolves to the main checkout's copy
 * of the lib — so importing it for its value makes this module, and anything testing it, unrunnable
 * outside CI. Same reasoning the harness route gives for its own copy of these two strings.
 */
export type MeetingJoinPolicy = 'ALL_VIDEO_EVENTS' | 'REQUESTED_ONLY';


export interface MeetingConnection {
  /**
   * The Agent whose settings these are. Absent means the link never landed — the join gate refuses
   * every meeting until it does, and the Channels row has no agent to point at.
   */
  agentId?: string;
  joinPolicy: MeetingJoinPolicy;
  notesFromStart: boolean;
  connectedAt?: string;
}

export interface UpcomingMeeting {
  calendarEventId: string;
  calendarTokenId: string;
  calendarId: string;
  title?: string;
  startsAt: string;
  endsAt?: string;
  meetingUrl: string;
  participantCount: number;
  state: UpcomingMeetingState;
  meetingSessionId?: string;
}

export interface MeetingsSchedule {
  /**
   * Whether the server would let the clock join for this site at all — the product kill switch and its
   * allowlist. A connected site can still have this false, and the card has to say so: "you switched
   * her on" and "we have switched this on for you" are different facts, and only one of them is the
   * user's to change.
   *
   * **Renamed off the wire deliberately.** Upstream calls this `autoJoinEnabled`, one word away from
   * the `joinPolicy === 'ALL_VIDEO_EVENTS'` switch the card labels "join automatically" — and the two
   * are unrelated facts: this one is ours and the user cannot move it, that one is theirs and we never
   * do. Two near-identical names for those two things in one component is a mis-read waiting to
   * happen, so the wire name stops at `readSchedule`.
   */
  joiningAllowedForSite: boolean;
  connection: MeetingConnection | null;
  calendars: { calendarTokenId: string; email?: string }[];
  meetings: UpcomingMeeting[];
}

/** The body `/api/meetings/upcoming` answers with, before `readSchedule` renames its switch. */
interface MeetingsScheduleWire extends Omit<MeetingsSchedule, 'joiningAllowedForSite'> {
  autoJoinEnabled: boolean;
}

export const meetingsKeys = {
  schedule: ['meetings', 'schedule'] as const,
  connection: ['meetings', 'connection'] as const,
  history: ['meetings', 'history'] as const,
  /** The one-page lookup the chat header falls back to — see `lookupMeetingByConversation`. */
  historyLookup: ['meetings', 'history', 'lookup'] as const,
};

export class MeetingsActionError extends Error {
  constructor(public status: number) {
    super(`HTTP ${status}`);
  }
}

const SCHEDULE_URL = `${API_BASE}/api/meetings/upcoming`;
const CONNECTION_URL = `${API_BASE}/api/meetings/connection`;
const HISTORY_URL = `${API_BASE}/api/meetings/history`;
const REQUEST_URL = `${API_BASE}/api/meetings/request`;

async function readSchedule(): Promise<MeetingsSchedule> {
  const res = await apiFetch(SCHEDULE_URL, { credentials: 'include' });
  if (!res.ok) throw new MeetingsActionError(res.status);
  const json = (await res.json()) as Partial<MeetingsScheduleWire>;
  return {
    joiningAllowedForSite: json.autoJoinEnabled === true,
    connection: json.connection ?? null,
    calendars: json.calendars ?? [],
    // One row per meeting, not per calendar copy of it — the decision behind a row is stored per
    // (site, event) upstream, so two copies of one event are one decision rendered twice.
    meetings: dedupeMeetingsByEvent(json.meetings ?? []),
  };
}

/**
 * One read behind the whole card: the connection, the calendars it covers, the server switch and the
 * week's meetings all arrive together, so the card can never render a connected state against a stale
 * list or the other way round.
 *
 * Deliberately not refetched on window focus. The upstream re-arms capsules on its way out, so this
 * GET legitimately writes — a background refetch every time a tab regains focus would be arming work
 * nobody asked for.
 */
export function useMeetingsSchedule() {
  const query = useQuery({
    queryKey: meetingsKeys.schedule,
    queryFn: readSchedule,
    refetchOnWindowFocus: false,
  });
  return { schedule: query.data, loading: query.isLoading, error: query.isError, refetch: query.refetch };
}

/**
 * Which agent this site's meeting settings belong to, or undefined for a site that never connected.
 *
 * A connection-only read on purpose: an agent's page must not pull a week of calendar events just to
 * discover it is not the meetings agent. `enabled` is the caller's gate — every other agent's page
 * would otherwise spend a request learning the same thing.
 *
 * Not refetched on focus, for the reason the schedule read gives: this GET legitimately WRITES. It
 * links an unlinked connection on its way out, and a background refetch on every focus would be
 * repair work nobody asked for.
 */
export function useMeetingsAgentId(enabled: boolean): string | undefined {
  const query = useQuery({
    queryKey: meetingsKeys.connection,
    queryFn: readConnectionAgentId,
    refetchOnWindowFocus: false,
    enabled,
  });
  return query.data ?? undefined;
}

async function readConnectionAgentId(): Promise<string | null> {
  const res = await apiFetch(CONNECTION_URL, { credentials: 'include' });
  // A site outside the release answers 501 here. That is not an error worth surfacing on an agent's
  // page — it simply means no agent of theirs is the meetings agent.
  if (!res.ok) return null;
  const json = (await res.json()) as { connection?: MeetingConnection | null };
  return json.connection?.agentId ?? null;
}

async function connectionRequest(method: string, body?: unknown): Promise<void> {
  const res = await apiFetch(CONNECTION_URL, {
    method,
    credentials: 'include',
    ...(body === undefined ? {} : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  });
  if (!res.ok) throw new MeetingsActionError(res.status);
}

/**
 * Connect, the two switches, and disconnect.
 *
 * Every one of them invalidates the schedule rather than patching it: flipping automatic joining
 * changes the state of every row in the list at once, and a card that showed the new switch position
 * over the old row states would be showing a promise it had already broken.
 *
 * A first connect asks for `REQUESTED_ONLY` explicitly. Pressing Connect is consent to the feature,
 * not consent to her walking into every interview and 1:1 on the calendar — the user turns automatic
 * on with the switch, which is a second, separate decision.
 *
 * **`useMutation` rather than the house's imperative cache edits (CLAUDE.md, Data fetching), on
 * purpose.** That style exists so a write can be shown optimistically and rolled back, and it needs
 * the new value to be a local edit of the old one. None of these four are: flipping automatic joining
 * re-decides every row in the list server-side, and the row states are the answer, not the switch. An
 * optimistic patch here would be a promise the page has not been told it can keep, so all four
 * invalidate and wait, and the `isLoading` each one exposes is what disables its control meanwhile.
 */
export function useMeetingsChannelActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: meetingsKeys.schedule });

  const connect = useMutation({
    mutationFn: () => connectionRequest('POST', { joinPolicy: 'REQUESTED_ONLY' satisfies MeetingJoinPolicy }),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: (patch: { joinPolicy?: MeetingJoinPolicy; notesFromStart?: boolean }) => connectionRequest('POST', patch),
    onSuccess: invalidate,
  });
  const disconnect = useMutation({
    mutationFn: () => connectionRequest('DELETE'),
    onSuccess: invalidate,
  });
  const decide = useMutation({
    mutationFn: async (input: { meeting: UpcomingMeeting; decision: 'join' | 'skip' | null }) => {
      const res = await apiFetch(REQUEST_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          calendarEventId: input.meeting.calendarEventId,
          calendarTokenId: input.meeting.calendarTokenId,
          calendarId: input.meeting.calendarId,
          decision: input.decision,
        }),
      });
      if (!res.ok) throw new MeetingsActionError(res.status);
    },
    onSuccess: invalidate,
  });

  return { connect, update, disconnect, decide };
}

/**
 * One note a meeting produced, still in fields.
 *
 * The harness renders these into the write-up's markdown, and `renderMeetingSummary` flattens an
 * action item's owner and due date into a parenthetical sentence on the way — so the conversation
 * cannot be read back into structure. The history row keeps them, and this is that shape.
 */
export interface PastMeetingNote {
  kind: 'note' | 'action_item';
  text: string;
  owner?: string;
  due?: string;
}

/**
 * A meeting she has already been in. `recordedAt` is when the WRITE-UP landed, in epoch ms;
 * `startedAt` / `endedAt` are the MEETING's own span, from the transcript's first and last stamps. The
 * row shows the meeting's time and falls back to `recordedAt` only for a meeting recorded before the
 * span existed, or one in which nobody spoke.
 */
export interface PastMeeting {
  meetingSessionId: string;
  conversationId: string;
  title?: string;
  recordedAt: number;
  startedAt?: number;
  endedAt?: number;
  meetingUrl?: string;
  /** Display names from the room — text, never links. Absent on rows written before the field existed. */
  participants?: { name: string; isHost: boolean }[];
  speakers?: { name: string; isAgent: boolean }[];
  transcriptLines: number;
  notes: PastMeetingNote[];
  notesPath?: string;
  transcriptPath?: string;
}

/**
 * The history row names its files in the AGENT's form (`spaces/meetings/<folder>/notes.md`); the
 * client's file readers take the STORED form the chat attachments carry (`workspaces/…`). Same file,
 * two spellings — this is the harness's `toStoredVfsPath`, so a row can read the file the card renders.
 */
export function meetingFileVfsPath(agentPath: string | undefined): string | undefined {
  if (!agentPath) return undefined;
  return agentPath.startsWith('spaces/') ? `workspaces/${agentPath.slice('spaces/'.length)}` : agentPath;
}

export interface MeetingHistory {
  meetings: PastMeeting[];
  /** Pass back as `before` for the next page. Absent means this was the last one. */
  cursor?: number;
}

/**
 * How many meetings a page asks for — a screenful, not the 50 the upstream would give. This is a
 * section on someone's agent page, and fifty rows of it is what the number is here to prevent.
 */
export const MEETING_HISTORY_PAGE = 10;

async function readMeetingHistory(before?: number): Promise<MeetingHistory> {
  const search = new URLSearchParams({ limit: String(MEETING_HISTORY_PAGE) });
  if (before !== undefined) search.set('before', String(before));
  const res = await apiFetch(`${HISTORY_URL}?${search}`, { credentials: 'include' });
  // A site outside the release answers 501, which is not an error worth a red line on her page — it
  // means no meeting of theirs was ever joined, so there is nothing to list. Same reasoning as the
  // connection read above.
  if (!res.ok) {
    if (res.status === 501) return { meetings: [] };
    throw new MeetingsActionError(res.status);
  }
  return (await res.json()) as MeetingHistory;
}

/**
 * The upstream's own page cap — the widest one read can look.
 */
const MEETING_HISTORY_LOOKUP_LIMIT = 50;

/**
 * Which meeting a conversation is the write-up of, from the history list rather than from the rows.
 *
 * The header normally learns this from the marker on the write-up row, but the chat loads a bounded
 * window (40 rows) and the marker sits on the FIRST row of the thread — so a meeting whose thread has
 * grown past the window shows no marker at all. This is the independent source: one read of the most
 * recent fifty meetings, cached, answering with the same facts the marker carries. A meeting older than
 * those fifty is not found; the header then draws its ordinary title, which is the honest degradation.
 *
 * Read through `queryClient.fetchQuery` so every long thread shares ONE request per five minutes.
 */
export async function lookupMeetingByConversation(conversationId: string): Promise<PastMeeting | null> {
  try {
    const page = await queryClient.fetchQuery({
      queryKey: meetingsKeys.historyLookup,
      queryFn: async () => {
        const search = new URLSearchParams({ limit: String(MEETING_HISTORY_LOOKUP_LIMIT) });
        const res = await apiFetch(`${HISTORY_URL}?${search}`, { credentials: 'include' });
        // 501 is the site being outside the release: there are no meetings, and caching that is right.
        if (res.status === 501) return { meetings: [] } as MeetingHistory;
        // Anything else — a 5xx, an auth answer while the session settles — must THROW rather than
        // resolve empty, or React Query caches "this account has no meetings" for five minutes and every
        // long thread opened in that window loses its header even after the service recovers.
        if (!res.ok) throw new MeetingsActionError(res.status);
        return (await res.json()) as MeetingHistory;
      },
      staleTime: 5 * 60_000,
    });
    return page.meetings.find((meeting) => meeting.conversationId === conversationId) ?? null;
  } catch {
    return null;
  }
}

/**
 * Her past meetings, a page at a time.
 *
 * Fetched HERE rather than in `useAgentDetail`, because only one agent on the account is the meetings
 * agent: a query in the shared hook would run on every agent's page and answer nothing on all but
 * one. The section that calls this only mounts behind `isMeetingsAgent`, so mounting IS the gate and
 * an ungated account spends no request.
 *
 * Paged on the store's own cursor — keyset on `recordedAt`, so a meeting written up while someone is
 * reading cannot shift a page boundary and make a row appear twice or not at all.
 *
 * **The cursor is stepped by one, and the rows are deduped — the two go together.** The cursor is a
 * TIMESTAMP and the store filters `lessThan` it, so two meetings recorded in the same millisecond can
 * straddle a boundary and the second is stepped straight over, unreachable at any page size. Asking
 * for `cursor + 1` asks for `<= cursor` instead, which hands the boundary row back a second time; the
 * `seen` set below is what makes that free. Dropping either half reintroduces a bug: without the step
 * a tied meeting is lost, without the set it is shown twice.
 */
export function useMeetingHistory() {
  const query = useInfiniteQuery({
    queryKey: meetingsKeys.history,
    queryFn: ({ pageParam }: { pageParam?: number }) => readMeetingHistory(pageParam),
    getNextPageParam: (last: MeetingHistory) => (last.cursor === undefined ? undefined : last.cursor + 1),
    refetchOnWindowFocus: false,
  });

  const { meetings, stalled } = foldMeetingPages(query.data?.pages ?? []);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  return {
    meetings,
    isLoading: query.isLoading,
    /**
     * A read failed — the first one, or a page. Deliberately NOT narrowed to the first: a failed
     * `fetchNextPage` leaves `data.pages` in place, so the caller can keep the rows it already has
     * and still report the failure. Which of the two it was is `meetings.length`.
     */
    isError: query.isError,
    /**
     * Whether pressing again can actually produce something. The server stops sending a cursor at the
     * end of the history; `stalled` covers the other ending, where it keeps sending one but every row
     * behind it has already been shown (see `foldMeetingPages`).
     */
    hasMore: Boolean(hasNextPage) && !stalled,
    isLoadingMore: isFetchingNextPage,
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
    },
  };
}
